import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { authFetch, API_BASE } from "../auth";
import { contractsFor, daysToExpiry, legValue } from "../utils/blackScholes";
import "./PriceBasedRunningMan.css";
import "./Simulation.css";
import TickerAutocomplete from "../components/TickerAutocomplete";
import TickerLabel from "../components/TickerLabel";

const LEGS = ["core", "lotto", "hedge"];
const SCENARIO_MOVES = [-10, -5, -2, -1, -0.5, 0, 0.5, 1, 2, 5, 10];
const SLIDER_RANGE_PCT = 25;
const DEFAULT_STRIKE_PCT = { core: "1", lotto: "3", hedge: "1" };
const DEFAULT_AMOUNT = "1000";
// Visual tag only - does not change pricing.
const FACTORS = { none: "None", pre: "Pre-event", post: "Post-event", momentum: "Momentum" };

function parseTickers(raw) {
  return raw
    .split(",")
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
}

function createEmptyTicker(symbol) {
  const legs = {};
  LEGS.forEach((leg) => {
    legs[leg] = {
      strike_mode: "pct",
      strike_value: DEFAULT_STRIKE_PCT[leg],
      direction: "",
      amount: DEFAULT_AMOUNT,
      strike: "",
      entry: "",
      iv: "",
    };
  });
  return { symbol, expiry_date: "", current_price: "", sim_price: "", factor: "none", error: null, legs };
}

const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));

function fmtUsd(v, signed = false) {
  if (!Number.isFinite(v)) return "—";
  const s = Math.abs(v).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  if (!signed) return v < 0 ? `-${s}` : s;
  return `${v > 0 ? "+" : v < 0 ? "-" : ""}${s}`;
}

function fmtPct(v) {
  if (!Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}

const pnlClass = (v) => (v > 0 ? "simPos" : v < 0 ? "simNeg" : "");

function cstStamp(d = new Date()) {
  return d.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

// Browser print dialog -> "Save as PDF". The title becomes the default PDF filename.
function printSimulation() {
  const prev = document.title;
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  document.title = `Simulation ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}${pad(now.getMinutes())}`;
  window.addEventListener("afterprint", () => { document.title = prev; }, { once: true });
  window.print();
}

// Strike used for pricing: the loaded/typed strike, else derived from the mode (not snapped to a chain).
function effectiveStrike(leg, price) {
  const typed = num(leg.strike);
  if (typed > 0) return typed;
  const v = num(leg.strike_value);
  if (!(price > 0) || !Number.isFinite(v) || !leg.direction) return NaN;
  const sign = leg.direction === "C" ? 1 : -1;
  if (leg.strike_mode === "exact") return v;
  if (leg.strike_mode === "dollar") return price + sign * v;
  return price * (1 + (sign * v) / 100);
}

// Legs that have enough inputs to price, in model form.
function pricedLegs(t) {
  const price = num(t.current_price);
  const dte = daysToExpiry(t.expiry_date);
  return LEGS.map((name) => {
    const l = t.legs[name];
    const entry = num(l.entry);
    const leg = {
      name,
      right: l.direction,
      strike: effectiveStrike(l, price),
      entry,
      iv: num(l.iv) / 100,
      contracts: contractsFor(num(l.amount), entry),
      dte,
    };
    const ok = leg.right && leg.strike > 0 && entry > 0 && leg.iv > 0 && leg.contracts > 0;
    return ok ? leg : null;
  }).filter(Boolean);
}

function tickerTotals(legs, S, opts) {
  return legs.reduce(
    (acc, leg) => {
      const v = legValue(leg, S, opts);
      acc.cost += v.cost;
      acc.value += v.value;
      acc.pnl += v.pnl;
      return acc;
    },
    { cost: 0, value: 0, pnl: 0 }
  );
}

/* ---------- Payoff chart (inline SVG) ---------- */

function PayoffChart({ legs, current, simPrice, opts }) {
  const W = 1000;
  const H = 240;
  const PAD = { l: 64, r: 12, t: 12, b: 24 };
  const lo = current * (1 - SLIDER_RANGE_PCT / 100);
  const hi = current * (1 + SLIDER_RANGE_PCT / 100);
  const N = 80;

  const xs = Array.from({ length: N + 1 }, (_, i) => lo + ((hi - lo) * i) / N);
  const maxDte = Math.max(...legs.map((l) => l.dte));
  const now = xs.map((S) => tickerTotals(legs, S, opts).pnl);
  const atExp = xs.map((S) => tickerTotals(legs, S, { ...opts, daysForward: maxDte }).pnl);

  let yMin = Math.min(0, ...now, ...atExp);
  let yMax = Math.max(0, ...now, ...atExp);
  if (yMax === yMin) yMax = yMin + 1;
  const pad = (yMax - yMin) * 0.05;
  yMin -= pad;
  yMax += pad;

  const px = (S) => PAD.l + ((S - lo) / (hi - lo)) * (W - PAD.l - PAD.r);
  const py = (v) => PAD.t + ((yMax - v) / (yMax - yMin)) * (H - PAD.t - PAD.b);
  const path = (ys) => xs.map((S, i) => `${i ? "L" : "M"}${px(S).toFixed(1)},${py(ys[i]).toFixed(1)}`).join("");
  const simPnl = tickerTotals(legs, simPrice, opts).pnl;

  return (
    <svg className="simChart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="P&L versus stock price">
      <line className="simChart__zero" x1={PAD.l} x2={W - PAD.r} y1={py(0)} y2={py(0)} />
      <line className="simChart__current" x1={px(current)} x2={px(current)} y1={PAD.t} y2={H - PAD.b} />
      <path className="simChart__expiry" d={path(atExp)} />
      <path className="simChart__now" d={path(now)} />
      <circle className="simChart__marker" cx={px(simPrice)} cy={py(simPnl)} r="5" />
      <text className="simChart__label" x={PAD.l - 6} y={py(yMax - pad) + 4} textAnchor="end">{fmtUsd(yMax - pad)}</text>
      <text className="simChart__label" x={PAD.l - 6} y={py(yMin + pad) + 4} textAnchor="end">{fmtUsd(yMin + pad)}</text>
      <text className="simChart__label" x={PAD.l} y={H - 6}>{lo.toFixed(2)}</text>
      <text className="simChart__label" x={px(current)} y={H - 6} textAnchor="middle">{current.toFixed(2)}</text>
      <text className="simChart__label" x={W - PAD.r} y={H - 6} textAnchor="end">{hi.toFixed(2)}</text>
    </svg>
  );
}

PayoffChart.propTypes = {
  legs: PropTypes.arrayOf(PropTypes.object).isRequired,
  current: PropTypes.number.isRequired,
  simPrice: PropTypes.number.isRequired,
  opts: PropTypes.object.isRequired,
};

/* ---------- Page ---------- */

export default function Simulation() {
  const [tickerInput, setTickerInput] = useState("");
  const [tickers, setTickers] = useState([]);
  const [rate, setRate] = useState("4.5");
  const [daysForward, setDaysForward] = useState(0);
  const [ivShift, setIvShift] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const updateTicker = (symbol, patch) =>
    setTickers((prev) => prev.map((t) => (t.symbol === symbol ? { ...t, ...patch } : t)));

  const updateLeg = (symbol, legName, field, value) =>
    setTickers((prev) =>
      prev.map((t) => {
        if (t.symbol !== symbol) return t;
        const legs = { ...t.legs, [legName]: { ...t.legs[legName], [field]: value } };
        // Strike inputs changed -> the loaded chain strike/premium no longer applies.
        if (["strike_mode", "strike_value", "direction"].includes(field)) {
          legs[legName] = { ...legs[legName], strike: "", entry: "", iv: "" };
        }
        if (legName === "core" && field === "direction") {
          const opposite = value === "C" ? "P" : value === "P" ? "C" : "";
          legs.lotto = { ...legs.lotto, direction: value, strike: "", entry: "", iv: "" };
          legs.hedge = { ...legs.hedge, direction: opposite, strike: "", entry: "", iv: "" };
        }
        return { ...t, legs };
      })
    );

  const addTickers = () => {
    const existing = new Set(tickers.map((t) => t.symbol));
    const fresh = parseTickers(tickerInput).filter((s) => !existing.has(s));
    if (fresh.length) setTickers((prev) => [...prev, ...fresh.map(createEmptyTicker)]);
    setTickerInput("");
  };

  const removeTicker = (symbol) => setTickers((prev) => prev.filter((t) => t.symbol !== symbol));

  const loadQuotes = async () => {
    const payload = tickers
      .filter((t) => t.expiry_date)
      .map((t) => {
        const legs = {};
        LEGS.forEach((name) => {
          const l = t.legs[name];
          if (l.direction && l.strike_value !== "" && num(l.amount) > 0) {
            legs[name] = { strike_mode: l.strike_mode, strike_value: l.strike_value, direction: l.direction };
          }
        });
        return { symbol: t.symbol, expiry_date: t.expiry_date, legs };
      });
    if (!payload.length) {
      setStatus({ type: "error", msg: "Set an expiry date on at least one ticker first." });
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      const res = await authFetch(`${API_BASE}/simulation/quote/`, {
        method: "POST",
        body: JSON.stringify({ tickers: payload }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);

      const bySymbol = Object.fromEntries(body.results.map((r) => [r.symbol, r]));
      setTickers((prev) =>
        prev.map((t) => {
          const r = bySymbol[t.symbol];
          if (!r) return t;
          const legs = { ...t.legs };
          const legErrors = [];
          Object.entries(r.legs || {}).forEach(([name, q]) => {
            if (q.error) legErrors.push(`${name}: ${q.error}`);
            const entry = q.ask ?? q.mid;
            legs[name] = {
              ...legs[name],
              strike: q.strike ?? legs[name].strike,
              entry: entry ?? legs[name].entry,
              iv: q.iv != null ? (q.iv * 100).toFixed(1) : legs[name].iv,
            };
          });
          const price = r.current_price ?? t.current_price;
          return {
            ...t,
            legs,
            current_price: price,
            sim_price: price,
            error: [r.error, ...legErrors].filter(Boolean).join("; ") || null,
          };
        })
      );
      setStatus({ type: "success", msg: "Loaded from IBKR. Any field below can be overridden." });
    } catch (e) {
      setStatus({ type: "error", msg: `Load failed: ${e.message}. You can still enter values manually.` });
    } finally {
      setLoading(false);
    }
  };

  const opts = useMemo(
    () => ({ daysForward, ivShift: ivShift / 100, r: (num(rate) || 0) / 100 }),
    [daysForward, ivShift, rate]
  );

  const models = useMemo(
    () =>
      tickers.map((t) => {
        const legs = pricedLegs(t);
        const current = num(t.current_price);
        const sim = num(t.sim_price) > 0 ? num(t.sim_price) : current;
        const ready = legs.length > 0 && current > 0;
        return { t, legs, current, sim, ready, totals: ready ? tickerTotals(legs, sim, opts) : null };
      }),
    [tickers, opts]
  );

  const ready = models.filter((m) => m.ready);
  const combined = ready.reduce(
    (acc, m) => ({ cost: acc.cost + m.totals.cost, pnl: acc.pnl + m.totals.pnl }),
    { cost: 0, pnl: 0 }
  );
  const maxDte = Math.ceil(Math.max(0, ...ready.flatMap((m) => m.legs.map((l) => l.dte))));

  return (
    <div className="pbForm simPage" id="sim-print-area">
      {/* ── Print-only header ── */}
      <header className="simPrintOnly simPrintHeader">
        <div className="simPrintHeader__brand">StreetAdvise</div>
        <div className="simPrintHeader__title">Options Simulation</div>
        <div className="simPrintHeader__meta">
          <span>Generated {cstStamp()} CST</span>
          <span>Risk-free {rate || 0}%</span>
          <span>Days forward {daysForward}</span>
          <span>IV shift {ivShift > 0 ? "+" : ""}{ivShift} vol pts</span>
          <span>Black-Scholes estimate. No orders placed.</span>
        </div>
      </header>

      {/* ── Setup ── */}
      <section className="pbCard simNoPrint">
        <div className="pbCard__head">
          <h2 className="pbCard__title">Simulation setup</h2>
          <span className="pbMeta">Black-Scholes what-if. No orders are placed.</span>
        </div>
        <div className="simRow">
          <TickerAutocomplete
            className="pbControl"
            placeholder="Tickers, comma separated (e.g. SPY, QQQ, AMD, AAPL)"
            value={tickerInput}
            onChange={setTickerInput}
            onKeyDown={(e) => e.key === "Enter" && addTickers()}
            aria-label="Tickers"
          />
          <button type="button" className="pbGhostBtn" onClick={addTickers}>Add</button>
          <label className="simInline">
            Risk-free %
            <input className="pbControl simNarrow" type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} />
          </label>
          <button type="button" className="pbPrimaryBtn" onClick={loadQuotes} disabled={loading || !tickers.length}>
            {loading ? "Loading…" : "Load from IBKR"}
          </button>
        </div>
        {status && <div className={`pbStatus pbStatus--${status.type} simStatus`}>{status.msg}</div>}
      </section>

      {/* ── Portfolio summary + global controls ── */}
      {tickers.length > 0 && (
        <section className="pbCard simSummary">
          <div className="simSummary__totals">
            <div>
              <div className="simStat__label">Total cost</div>
              <div className="simStat__value">{fmtUsd(combined.cost)}</div>
            </div>
            <div>
              <div className="simStat__label">Combined P&amp;L</div>
              <div className={`simStat__value ${pnlClass(combined.pnl)}`}>
                {fmtUsd(combined.pnl, true)} <span className="simStat__pct">{fmtPct(combined.cost ? (combined.pnl / combined.cost) * 100 : NaN)}</span>
              </div>
            </div>
            {ready.map((m) => (
              <div key={m.t.symbol}>
                <div className="simStat__label">{m.t.symbol}</div>
                <div className={`simStat__small ${pnlClass(m.totals.pnl)}`}>{fmtUsd(m.totals.pnl, true)}</div>
              </div>
            ))}
          </div>
          <div className="simSliders">
            <label className="simSlider">
              <span>Days forward: <strong>{daysForward}</strong>{maxDte ? ` / ${maxDte}` : ""}</span>
              <input type="range" min="0" max={Math.max(maxDte, 1)} step="1" value={daysForward}
                onChange={(e) => setDaysForward(Number(e.target.value))} />
            </label>
            <label className="simSlider">
              <span>IV shift: <strong>{ivShift > 0 ? "+" : ""}{ivShift} vol pts</strong></span>
              <input type="range" min="-50" max="50" step="1" value={ivShift}
                onChange={(e) => setIvShift(Number(e.target.value))} />
            </label>
            <div className="simSliders__actions simNoPrint">
              <button
                type="button"
                className="pbGhostBtn"
                onClick={() => {
                  setDaysForward(0);
                  setIvShift(0);
                  setTickers((prev) => prev.map((t) => ({ ...t, sim_price: t.current_price })));
                }}
              >
                Reset all
              </button>
              <button type="button" className="pbGhostBtn" onClick={printSimulation}>
                Print / Save PDF
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Scenario grid ── */}
      {ready.length > 0 && (
        <section className="pbCard">
          <div className="pbCard__head">
            <h2 className="pbCard__title">Scenario grid</h2>
            <span className="pbMeta">P&amp;L if every ticker moves by the same % (uses days-forward and IV shift)</span>
          </div>
          <div className="simTableWrap">
            <table className="simTable simTable--grid">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th className="simFactorCell">Factor</th>
                  {SCENARIO_MOVES.map((m) => <th key={m}>{m > 0 ? `+${m}` : m}%</th>)}
                </tr>
              </thead>
              <tbody>
                {ready.map((m) => (
                  <tr key={m.t.symbol} className={`simFactor--${m.t.factor}`}>
                    <td className="simFactorTicker">{m.t.symbol}</td>
                    <td className="simFactorCell">
                      <select
                        className="pbControl simFactorSelect"
                        value={m.t.factor}
                        onChange={(e) => updateTicker(m.t.symbol, { factor: e.target.value })}
                        aria-label={`${m.t.symbol} driving factor`}
                      >
                        {Object.entries(FACTORS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                    </td>
                    {SCENARIO_MOVES.map((mv) => {
                      const p = tickerTotals(m.legs, m.current * (1 + mv / 100), opts).pnl;
                      return <td key={mv} className={pnlClass(p)}>{fmtUsd(p, true)}</td>;
                    })}
                  </tr>
                ))}
                <tr className="simTable__total">
                  <td>Combined</td>
                  <td className="simFactorCell" />
                  {SCENARIO_MOVES.map((mv) => {
                    const p = ready.reduce((s, m) => s + tickerTotals(m.legs, m.current * (1 + mv / 100), opts).pnl, 0);
                    return <td key={mv} className={pnlClass(p)}>{fmtUsd(p, true)}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="simFactorLegend">
            {Object.entries(FACTORS).filter(([k]) => k !== "none").map(([k, label]) => (
              <span key={k} className={`simFactorLegend__item simFactor--${k}`}>{label}</span>
            ))}
          </div>
        </section>
      )}

      {/* ── Ticker cards ── */}
      {models.map(({ t, legs, current, sim, ready: tReady, totals }) => {
        const pricedByName = Object.fromEntries(legs.map((l) => [l.name, l]));
        const move = current > 0 ? ((sim - current) / current) * 100 : NaN;
        return (
          <section
            key={t.symbol}
            className={`pbTickerCard${t.legs.core.direction === "C" ? " pbTickerCard--call" : t.legs.core.direction === "P" ? " pbTickerCard--put" : ""}`}
          >
            <div className="pbTickerCard__head">
              <div>
                <div className="pbTicker"><TickerLabel symbol={t.symbol} /></div>
                {tReady && (
                  <div className={`simTickerPnl ${pnlClass(totals.pnl)}`}>
                    {fmtUsd(totals.pnl, true)} ({fmtPct(totals.cost ? (totals.pnl / totals.cost) * 100 : NaN)}) on {fmtUsd(totals.cost)}
                  </div>
                )}
              </div>
              <button type="button" className="pbGhostBtn" onClick={() => removeTicker(t.symbol)}>Remove</button>
            </div>

            {t.error && <div className="pbStatus pbStatus--error simStatus">{t.error}</div>}

            <div className="pbGrid pbGrid--3">
              <label className="pbField">
                <span className="pbField__label">Expiry date</span>
                <input className="pbControl" type="date" value={t.expiry_date}
                  onChange={(e) => updateTicker(t.symbol, { expiry_date: e.target.value })} />
              </label>
              <label className="pbField">
                <span className="pbField__label">Current price</span>
                <input className="pbControl" type="number" step="0.01" value={t.current_price}
                  onChange={(e) => updateTicker(t.symbol, { current_price: e.target.value, sim_price: e.target.value })} />
              </label>
              <label className="pbField">
                <span className="pbField__label">Simulated price</span>
                <input className="pbControl" type="number" step="0.01" value={t.sim_price}
                  disabled={!(current > 0)}
                  onChange={(e) => updateTicker(t.symbol, { sim_price: e.target.value })} />
              </label>
            </div>

            {current > 0 && (
              <div className="simPriceSlider">
                <input
                  type="range"
                  min={current * (1 - SLIDER_RANGE_PCT / 100)}
                  max={current * (1 + SLIDER_RANGE_PCT / 100)}
                  step={current / 1000}
                  value={sim}
                  onChange={(e) => updateTicker(t.symbol, { sim_price: Number(e.target.value).toFixed(2) })}
                  aria-label={`${t.symbol} simulated price`}
                />
                <span className={`simMove ${pnlClass(move)}`}>{fmtPct(move)}</span>
                <button type="button" className="pbGhostBtn pbGhostBtn--sm" onClick={() => updateTicker(t.symbol, { sim_price: t.current_price })}>
                  Reset
                </button>
              </div>
            )}

            <div className="simTableWrap">
              <table className="simTable simLegTable">
                <thead>
                  <tr>
                    <th>Leg</th><th>Dir</th><th>Strike mode</th><th>Value</th><th>Amount $</th>
                    <th>Strike</th><th>Entry</th><th>IV %</th><th>Qty</th><th>Sim price</th><th>P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {LEGS.map((name) => {
                    const l = t.legs[name];
                    const p = pricedByName[name];
                    const v = p ? legValue(p, sim, opts) : null;
                    const derived = effectiveStrike({ ...l, strike: "" }, current);
                    const needsIv = l.direction && num(l.amount) > 0 && !(num(l.iv) > 0);
                    return (
                      <tr key={name}>
                        <td className="simLegName">{name}</td>
                        <td>
                          <select className="pbControl" value={l.direction} disabled={name !== "core" && !!t.legs.core.direction}
                            onChange={(e) => updateLeg(t.symbol, name, "direction", e.target.value)} aria-label={`${name} direction`}>
                            <option value="">—</option>
                            <option value="C">Call</option>
                            <option value="P">Put</option>
                          </select>
                        </td>
                        <td>
                          <select className="pbControl" value={l.strike_mode}
                            onChange={(e) => updateLeg(t.symbol, name, "strike_mode", e.target.value)} aria-label={`${name} strike mode`}>
                            <option value="pct">% from price</option>
                            <option value="dollar">$ deviation</option>
                            <option value="exact">Exact strike</option>
                          </select>
                        </td>
                        <td><input className="pbControl" type="number" step="0.01" value={l.strike_value}
                          onChange={(e) => updateLeg(t.symbol, name, "strike_value", e.target.value)} aria-label={`${name} strike value`} /></td>
                        <td><input className="pbControl" type="number" step="1" value={l.amount}
                          onChange={(e) => updateLeg(t.symbol, name, "amount", e.target.value)} aria-label={`${name} amount`} /></td>
                        <td><input className="pbControl" type="number" step="0.5" value={l.strike}
                          placeholder={Number.isFinite(derived) ? derived.toFixed(2) : ""}
                          onChange={(e) => updateLeg(t.symbol, name, "strike", e.target.value)}
                          aria-label={`${name} strike`} /></td>
                        <td><input className="pbControl" type="number" step="0.01" value={l.entry}
                          onChange={(e) => updateLeg(t.symbol, name, "entry", e.target.value)} aria-label={`${name} entry premium`} /></td>
                        <td className={needsIv ? "pbField--error" : ""}><input className="pbControl" type="number" step="0.1" value={l.iv}
                          onChange={(e) => updateLeg(t.symbol, name, "iv", e.target.value)} aria-label={`${name} implied volatility`} /></td>
                        <td>{p ? p.contracts : "—"}</td>
                        <td>{v ? v.price.toFixed(2) : "—"}</td>
                        <td className={v ? pnlClass(v.pnl) : ""}>
                          {v ? <>{fmtUsd(v.pnl, true)}<div className="simSub">{fmtPct((v.pnl / v.cost) * 100)}</div></> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {tReady && (
              <>
                <PayoffChart legs={legs} current={current} simPrice={sim} opts={opts} />
                <div className="simLegend">
                  <span className="simLegend__now">At days-forward / IV shift</span>
                  <span className="simLegend__expiry">At expiry</span>
                </div>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}
